import { useState } from 'react';
import { describeError } from '../../api/errors';
import { useDecideOrderChange, useOrderChanges } from '../../api/hooks';
import type { OrderChangeRequestRow } from '../../api/types';
import { Alert, Button, Chip, Field, Money, Panel, Table } from '../../ui';

const TONE = { PENDING: 'us', APPROVED: 'done', REJECTED: 'stop' } as const;
const LABEL = { PENDING: 'Pending approval', APPROVED: 'Approved', REJECTED: 'Rejected' } as const;

/**
 * A-15 — the changes a retailer has asked for on this order: who, when, each line's previous and
 * requested value, and the status. Nothing here has touched the order until it is approved; approving
 * applies the change and re-totals the order, rejecting leaves the order as confirmed and tells the
 * retailer why. Deciding needs `order.approve`.
 */
export function ChangeRequestsAdmin({ orderId, currency, canDecide }: { orderId: string; currency: string; canDecide: boolean }) {
  const changes = useOrderChanges('admin', orderId);
  const decide = useDecideOrderChange(orderId);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rows = changes.data?.requests ?? [];
  if (rows.length === 0) return null;

  async function run(r: OrderChangeRequestRow, decision: 'approve' | 'reject') {
    setError(null);
    try {
      await decide.mutateAsync({ requestId: r.id, decision, ...(decision === 'reject' ? { reason } : {}) });
      setRejecting(null); setReason('');
    } catch (e) { setError(describeError(e)); }
  }

  return (
    <Panel title="Change requests from the retailer" pad>
      {error ? <Alert tone="stop">{error}</Alert> : null}
      {rows.map((r) => (
        <div key={r.id} style={{ marginBottom: 'var(--mk-space-5)' }}>
          <div>
            <Chip tone={TONE[r.status]}>{LABEL[r.status]}</Chip>{' '}
            <span className="mk-dim">
              Requested by {r.requestedBy ?? 'the retailer'} on {new Date(r.requestedAt).toLocaleString()}
              {r.totalWhenAsked ? <> · order total then <Money amount={r.totalWhenAsked} currency={currency} /></> : null}
            </span>
          </div>
          <Table
            rows={r.lines}
            rowKey={(l) => l.lineId}
            columns={[
              { header: 'Item / Product Name', render: (l) => l.description ?? '—' },
              { header: 'Variant', render: (l) => <code>{l.variantCode ?? '—'}</code> },
              { header: 'Pieces (previous → requested)', render: (l) => (l.quantityFrom === l.quantityTo ? l.quantityTo : `${l.quantityFrom} → ${l.quantityTo}`) },
              { header: 'Size (previous → requested)', render: (l) => (l.sizeFrom === l.sizeTo ? (l.sizeTo ?? '—') : `${l.sizeFrom ?? '—'} → ${l.sizeTo ?? '—'}`) },
            ]}
          />
          {r.status !== 'PENDING' ? (
            <div className="mk-dim">
              {r.status === 'APPROVED' ? 'Approved' : 'Rejected'} by {r.decidedBy ?? 'Miraki'}
              {r.decidedAt ? ` on ${new Date(r.decidedAt).toLocaleString()}` : ''}
              {r.decisionReason ? ` — ${r.decisionReason}` : ''}
            </div>
          ) : canDecide ? (
            rejecting === r.id ? (
              <div style={{ marginTop: 8 }}>
                <Field label="Reason for rejecting (shown to the retailer)">
                  <input className="mk-fld" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button disabled={reason.trim().length < 3 || decide.isPending} onClick={() => void run(r, 'reject')}>Confirm rejection</Button>
                  <Button tone="ghost" onClick={() => { setRejecting(null); setReason(''); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button disabled={decide.isPending} onClick={() => void run(r, 'approve')}>Approve change</Button>
                <Button tone="ghost" disabled={decide.isPending} onClick={() => setRejecting(r.id)}>Reject</Button>
              </div>
            )
          ) : null}
        </div>
      ))}
    </Panel>
  );
}
